import {
    Alert,
    Autocomplete,
    Box,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    Button,
} from '@mui/material';
import { useState, useEffect, useMemo } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { subDays } from 'date-fns';
import { de } from 'date-fns/locale';

import {
    firebaseCandlesToMarketAnalysisData,
    clearMetadataCache,
    fetchPolymarketMetadata,
    extractPolymarketEvents,
    extractPolymarketMarkets,
    loadEventsFromCache,
} from './odds/firebaseDataProvider';
import {
    MarketAnalysisData,
    SeriesVisibility,
    VolumeVisibility,
    CandleFrequency,
    CandleDataPoint,
    PolymarketCandleMetadata,
    PolymarketEvent,
    PolymarketMarket,
} from './odds/types';
import { loadFullSeriesData } from './odds/cloudFunctionClient';
import { filterByDateRange } from './odds/csvDataLoader';
import {
    padCandles,
    aggregateCandles,
    getAvailableFrequencies,
    snapToStandardFrequency,
    CANDLE_FREQUENCIES,
} from './odds/candleUtils';
import {
    SeriesToggleControls,
    VolumeToggleControls,
    FrequencySelector,
} from './odds/components';
import { OddsPerformanceChart } from './odds/OddsPerformanceChart';

const OddsTab = () => {
    // ==================== STATE MANAGEMENT ====================
    const [singleMarketCategory, setSingleMarketCategory] =
        useState<string>('Crypto');
    const [singleMarketFromDate, setSingleMarketFromDate] = useState<Date>(
        subDays(new Date(), 30)
    );
    const [singleMarketToDate, setSingleMarketToDate] = useState<Date>(
        new Date()
    );
    const [baseSingleMarketFromDate, setBaseSingleMarketFromDate] =
        useState<Date>(subDays(new Date(), 30));
    const [baseSingleMarketToDate, setBaseSingleMarketToDate] = useState<Date>(
        new Date()
    );
    const [isSingleMarketZoomed, setIsSingleMarketZoomed] =
        useState<boolean>(false);

    const [seriesVisibility, setSeriesVisibility] = useState<SeriesVisibility>({
        bidLine: true,
        askLine: true,
        fairValueLine: true,
        uncertaintyBand: true,
        trades: true,
        candles: true,
    });
    const [volumeVisibility, setVolumeVisibility] = useState<VolumeVisibility>({
        showVolume: true,
        metric: 'volume',
    });

    // Candle frequency state
    const [selectedCandleFrequency, setSelectedCandleFrequency] =
        useState<CandleFrequency | null>(null);
    const [baseFrequencyMs, setBaseFrequencyMs] = useState<number | null>(null);

    // Current market's candle data (single market, no caching)
    const [currentCandleData, setCurrentCandleData] = useState<CandleDataPoint[] | null>(null);

    // Error state for fetch failures
    const [fetchError, setFetchError] = useState<string | null>(null);

    // ==================== POLYMARKET METADATA STATE ====================
    const [polymarketMetadata, setPolymarketMetadata] = useState<PolymarketCandleMetadata[]>([]);
    const [polymarketEvents, setPolymarketEvents] = useState<PolymarketEvent[]>([]);
    const [selectedPolymarketEvent, setSelectedPolymarketEvent] = useState<PolymarketEvent | null>(null);
    const [polymarketMarkets, setPolymarketMarkets] = useState<PolymarketMarket[]>([]);
    const [selectedPolymarketMarket, setSelectedPolymarketMarket] = useState<PolymarketMarket | null>(null);
    const [cloudFunctionLoading, setCloudFunctionLoading] = useState<boolean>(false);
    const [metadataLoading, setMetadataLoading] = useState<boolean>(true);

    const dateRangeButtons = [
        { label: '30 days', days: 30 },
        { label: '60 days', days: 60 },
        { label: '90 days', days: 90 },
        { label: '180 days', days: 180 },
        { label: '1 year', days: 365 },
        { label: '2 years', days: 730 },
        { label: '5 years', days: 1825 },
        { label: 'All', days: 10000 },
    ];

    // ==================== DATA FETCHING ====================

    // Fetch Polymarket metadata on mount with stale-while-revalidate for events
    useEffect(() => {
        let isMounted = true;

        // Immediately load cached events for instant dropdown population
        const cachedEvents = loadEventsFromCache();
        if (cachedEvents && cachedEvents.length > 0) {
            setPolymarketEvents(cachedEvents);
            console.log(`[Odds] Loaded ${cachedEvents.length} cached events from localStorage`);
        }

        // Fetch full metadata in background (updates events when done)
        const fetchMetadata = async () => {
            setFetchError(null);
            try {
                const metadata = await fetchPolymarketMetadata();
                if (isMounted) {
                    setPolymarketMetadata(metadata);
                    setMetadataLoading(false);
                    console.log(`[Odds] Loaded ${metadata.length} metadata documents`);
                }
            } catch (error) {
                console.error('Error fetching polymarket metadata:', error);
                if (isMounted) {
                    setMetadataLoading(false);
                    setFetchError('Failed to load metadata. Please try again.');
                }
            }
        };

        fetchMetadata();
        return () => {
            isMounted = false;
        };
    }, []);

    // Extract Polymarket events from metadata (grouped by event_title)
    useEffect(() => {
        if (polymarketMetadata.length === 0) return;

        const events = extractPolymarketEvents(polymarketMetadata);

        // Only update state if event list actually changed (prevents
        // unnecessary re-renders of the Autocomplete options)
        setPolymarketEvents(prev => {
            if (prev.length === events.length &&
                prev.every((e, i) => e.id === events[i].id)) {
                return prev;
            }
            return events;
        });
        console.log(`[Odds] Extracted ${events.length} events from metadata`);

        // Reset selection if the selected event no longer exists in fresh data
        setSelectedPolymarketEvent(prev => {
            if (prev && !events.find(e => e.id === prev.id)) {
                return events.length > 0 ? events[0] : null;
            }
            return prev;
        });

        // Auto-select first event
        if (events.length > 0 && !selectedPolymarketEvent) {
            setSelectedPolymarketEvent(events[0]);
        }
    }, [polymarketMetadata]);

    // Extract markets when a Polymarket event is selected
    useEffect(() => {
        if (!selectedPolymarketEvent || polymarketMetadata.length === 0) {
            setPolymarketMarkets([]);
            setSelectedPolymarketMarket(null);
            return;
        }

        const markets = extractPolymarketMarkets(polymarketMetadata, selectedPolymarketEvent.id);
        setPolymarketMarkets(markets);
        console.log(`[Odds] Extracted ${markets.length} markets for event ${selectedPolymarketEvent.displayName}`);

        // Auto-select first market
        if (markets.length > 0) {
            setSelectedPolymarketMarket(markets[0]);
        } else {
            setSelectedPolymarketMarket(null);
        }
    }, [selectedPolymarketEvent, polymarketMetadata]);

    // Load candle data via Cloud Function when a market is selected
    useEffect(() => {
        if (!selectedPolymarketMarket) {
            return;
        }

        // Clear previous market's data immediately to free memory
        setCurrentCandleData(null);

        const controller = new AbortController();
        const loadCandles = async () => {
            setCloudFunctionLoading(true);
            setFetchError(null);

            try {
                console.log(`[Odds] Loading candles for ${selectedPolymarketMarket.series}`);
                const candles = await loadFullSeriesData(
                    selectedPolymarketMarket.series,
                    controller.signal
                );

                if (!controller.signal.aborted) {
                    setCurrentCandleData(candles);
                    console.log(`[Odds] Loaded ${candles.length} candles`);

                    // Set date range from candle data
                    if (candles.length > 0) {
                        const minDate = candles[0].timestamp;
                        const maxDate = candles[candles.length - 1].timestamp;
                        setSingleMarketFromDate(minDate);
                        setSingleMarketToDate(maxDate);
                        setBaseSingleMarketFromDate(minDate);
                        setBaseSingleMarketToDate(maxDate);
                    }
                }
            } catch (error) {
                if (error instanceof DOMException && error.name === 'AbortError') {
                    return; // Silently ignore aborted requests
                }
                console.error('Error loading candle data:', error);
                if (!controller.signal.aborted) {
                    setFetchError('Failed to load candle data. Please try again.');
                }
            } finally {
                if (!controller.signal.aborted) {
                    setCloudFunctionLoading(false);
                }
            }
        };

        loadCandles();
        return () => {
            controller.abort();
        };
    }, [selectedPolymarketMarket]);

    // ==================== COMPUTED VALUES ====================

    // Convert Firebase candles to MarketAnalysisData format
    const firebaseAnalysisData = useMemo((): MarketAnalysisData | null => {
        if (!selectedPolymarketMarket || !currentCandleData || currentCandleData.length === 0) return null;
        return firebaseCandlesToMarketAnalysisData(currentCandleData);
    }, [selectedPolymarketMarket, currentCandleData]);

    // Unified analysis data
    const currentAnalysisData = useMemo((): MarketAnalysisData | null => {
        return firebaseAnalysisData;
    }, [firebaseAnalysisData]);

    // Compute data availability for SeriesToggleControls
    const seriesDataAvailability = useMemo(() => {
        const data = currentAnalysisData;
        return {
            bidLine: (data?.bidAsk?.length ?? 0) > 0,
            askLine: (data?.bidAsk?.length ?? 0) > 0,
            fairValueLine: (data?.fairValue?.length ?? 0) > 0,
            uncertaintyBand: (data?.fairValue?.length ?? 0) > 0,
            trades: (data?.trades?.length ?? 0) > 0,
            candles: (data?.candles?.length ?? 0) > 0,
        };
    }, [currentAnalysisData]);

    // Use metadata period as authoritative base frequency (not inferred from candle gaps)
    const { availableFrequencies, detectedBaseFrequency } = useMemo(() => {
        if (!selectedPolymarketMarket) {
            return {
                availableFrequencies: CANDLE_FREQUENCIES,
                detectedBaseFrequency: null,
            };
        }

        const periodMs = selectedPolymarketMarket.periodMs;
        const available = getAvailableFrequencies(periodMs);
        const snapped = snapToStandardFrequency(periodMs);

        return {
            availableFrequencies: available,
            detectedBaseFrequency: snapped,
        };
    }, [selectedPolymarketMarket]);

    // Set frequency when market changes (atomic: reset + init in one effect)
    useEffect(() => {
        if (!selectedPolymarketMarket) {
            setSelectedCandleFrequency(null);
            setBaseFrequencyMs(null);
            return;
        }

        const periodMs = selectedPolymarketMarket.periodMs;
        const snapped = snapToStandardFrequency(periodMs);
        setSelectedCandleFrequency(snapped.value);
        setBaseFrequencyMs(snapped.ms);
    }, [selectedPolymarketMarket?.id, selectedPolymarketMarket?.periodMs]);

    // Set series visibility when Polymarket data loads
    useEffect(() => {
        if (firebaseAnalysisData) {
            setSeriesVisibility({
                bidLine: false,
                askLine: false,
                fairValueLine: false,
                uncertaintyBand: false,
                trades: false,
                candles: true,
            });
        }
    }, [selectedPolymarketMarket?.id]);

    // Filter current analysis data by date range and aggregate candles
    const filteredCurrentAnalysisData = useMemo(() => {
        if (!currentAnalysisData) return null;

        // Filter candles by date range
        const filteredCandles = currentAnalysisData.candles
            ? filterByDateRange(
                  currentAnalysisData.candles,
                  singleMarketFromDate,
                  singleMarketToDate
              )
            : undefined;

        // Aggregate candles if a different frequency is selected
        let processedCandles = filteredCandles;
        if (
            filteredCandles &&
            filteredCandles.length > 0 &&
            selectedCandleFrequency &&
            baseFrequencyMs
        ) {
            const selectedFreq = CANDLE_FREQUENCIES.find(
                f => f.value === selectedCandleFrequency
            );
            if (selectedFreq && selectedFreq.ms > baseFrequencyMs) {
                processedCandles = aggregateCandles(
                    filteredCandles,
                    selectedFreq.ms
                );
            }
        }

        // Pad candles at the selected frequency (or base frequency from metadata)
        const selectedFreqMs = selectedCandleFrequency
            ? CANDLE_FREQUENCIES.find(f => f.value === selectedCandleFrequency)?.ms
            : baseFrequencyMs;

        const paddedCandles =
            processedCandles && processedCandles.length > 0
                ? padCandles(processedCandles, selectedFreqMs ?? undefined)
                : undefined;

        return {
            bidAsk: filterByDateRange(
                currentAnalysisData.bidAsk,
                singleMarketFromDate,
                singleMarketToDate
            ),
            fairValue: filterByDateRange(
                currentAnalysisData.fairValue,
                singleMarketFromDate,
                singleMarketToDate
            ),
            trades: filterByDateRange(
                currentAnalysisData.trades,
                singleMarketFromDate,
                singleMarketToDate
            ),
            candles: paddedCandles,
        };
    }, [
        currentAnalysisData,
        singleMarketFromDate,
        singleMarketToDate,
        selectedCandleFrequency,
        baseFrequencyMs,
    ]);

    // Selected frequency in milliseconds for chart renderers
    const candleFrequencyMs = useMemo(() => {
        if (!selectedCandleFrequency) return undefined;
        return CANDLE_FREQUENCIES.find(f => f.value === selectedCandleFrequency)?.ms;
    }, [selectedCandleFrequency]);

    // ==================== EVENT HANDLERS ====================

    const handleDateRangeClick = (days: number) => {
        if (!selectedPolymarketMarket || !currentCandleData || currentCandleData.length === 0) return;

        const maxDate = currentCandleData[currentCandleData.length - 1].timestamp;
        if (days >= 10000) {
            // "All" button - show full range
            const minDate = currentCandleData[0].timestamp;
            setSingleMarketFromDate(minDate);
            setSingleMarketToDate(maxDate);
            setBaseSingleMarketFromDate(minDate);
            setBaseSingleMarketToDate(maxDate);
        } else {
            const fromDate = subDays(maxDate, days);
            setSingleMarketFromDate(fromDate);
            setSingleMarketToDate(maxDate);
            setBaseSingleMarketFromDate(fromDate);
            setBaseSingleMarketToDate(maxDate);
        }
    };

    // ==================== RENDER ====================

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
            <Box pt={2} px={2}>
                {/* Error Alert */}
                {fetchError && (
                    <Alert
                        severity="error"
                        onClose={() => setFetchError(null)}
                        sx={{ mb: 2 }}
                    >
                        {fetchError}
                    </Alert>
                )}

                {/* Polymarket Selection Controls */}
                <Box mb={3}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel id="polymarket-category-label">
                                    Category
                                </InputLabel>
                                <Select
                                    labelId="polymarket-category-label"
                                    value={singleMarketCategory}
                                    onChange={e => {
                                        setSingleMarketCategory(e.target.value);
                                        setSelectedPolymarketEvent(null);
                                        setSelectedPolymarketMarket(null);
                                        setCurrentCandleData(null);
                                        clearMetadataCache();
                                    }}
                                    label="Category"
                                >
                                    <MenuItem value="Crypto">Crypto</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Autocomplete
                                value={selectedPolymarketEvent}
                                onChange={(_, event) => {
                                    setSelectedPolymarketEvent(event);
                                    setSelectedPolymarketMarket(null);
                                    setCurrentCandleData(null);
                                }}
                                options={polymarketEvents}
                                getOptionLabel={(option) => option.eventTitle || option.displayName}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                loading={metadataLoading && polymarketEvents.length === 0}
                                disabled={!metadataLoading && polymarketEvents.length === 0}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Event"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {metadataLoading && polymarketEvents.length === 0 ? (
                                                        <CircularProgress size={16} />
                                                    ) : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel id="polymarket-market-label">
                                    Market
                                </InputLabel>
                                <Select
                                    labelId="polymarket-market-label"
                                    value={selectedPolymarketMarket?.id || ''}
                                    onChange={e => {
                                        const market = polymarketMarkets.find(
                                            m => m.id === e.target.value
                                        );
                                        setSelectedPolymarketMarket(market || null);
                                    }}
                                    label="Market"
                                    disabled={polymarketMarkets.length === 0}
                                >
                                    {polymarketMarkets.map(market => (
                                        <MenuItem key={market.id} value={market.id}>
                                            {market.entity}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Grid
                        container
                        spacing={2}
                        alignItems="center"
                        sx={{ mt: 2 }}
                    >
                        <Grid item xs={6} md={4}>
                            <DatePicker
                                label="From"
                                value={singleMarketFromDate}
                                onChange={(newValue: Date | null) => {
                                    if (newValue) {
                                        setSingleMarketFromDate(newValue);
                                        setBaseSingleMarketFromDate(newValue);
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <DatePicker
                                label="To"
                                value={singleMarketToDate}
                                onChange={(newValue: Date | null) => {
                                    if (newValue) {
                                        setSingleMarketToDate(newValue);
                                        setBaseSingleMarketToDate(newValue);
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Box display="flex" gap={1}>
                                    {dateRangeButtons.slice(0, 4).map(range => (
                                        <Button
                                            key={range.days}
                                            variant="outlined"
                                            size="small"
                                            sx={{ width: 90 }}
                                            onClick={() =>
                                                handleDateRangeClick(
                                                    range.days
                                                )
                                            }
                                        >
                                            {range.label}
                                        </Button>
                                    ))}
                                </Box>
                                <Box display="flex" gap={1}>
                                    {dateRangeButtons.slice(4, 8).map(range => (
                                        <Button
                                            key={range.days}
                                            variant="outlined"
                                            size="small"
                                            sx={{ width: 90 }}
                                            onClick={() =>
                                                handleDateRangeClick(
                                                    range.days
                                                )
                                            }
                                        >
                                            {range.label}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Polymarket Chart Section */}
                {selectedPolymarketMarket && (
                    <Box mb={4}>
                        <Typography variant="subtitle2" gutterBottom>
                            {selectedPolymarketEvent?.eventTitle || selectedPolymarketEvent?.displayName}
                            {' - '}
                            {selectedPolymarketMarket.entity}
                        </Typography>
                        {cloudFunctionLoading ? (
                            <Box display="flex" justifyContent="center" py={4}>
                                <CircularProgress />
                            </Box>
                        ) : !currentAnalysisData ? (
                            <Box display="flex" justifyContent="center" py={4}>
                                <Typography color="text.secondary">
                                    No data available
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mb: 1,
                                    }}
                                >
                                    <SeriesToggleControls
                                        visibility={seriesVisibility}
                                        onChange={setSeriesVisibility}
                                        disabled={!currentAnalysisData}
                                        dataAvailability={seriesDataAvailability}
                                    />
                                    {currentAnalysisData?.candles &&
                                        currentAnalysisData.candles.length > 0 &&
                                        selectedCandleFrequency && (
                                            <FrequencySelector
                                                selectedFrequency={selectedCandleFrequency}
                                                availableFrequencies={availableFrequencies}
                                                onChange={setSelectedCandleFrequency}
                                                disabled={!seriesVisibility.candles}
                                            />
                                        )}
                                </Box>
                                <VolumeToggleControls
                                    visibility={volumeVisibility}
                                    onChange={setVolumeVisibility}
                                    disabled={!currentAnalysisData?.candles}
                                    disabledMetrics={{
                                        buySellVolume: !seriesDataAvailability.trades,
                                    }}
                                />
                                <OddsPerformanceChart
                                    data={[]}
                                    markets={[]}
                                    marketColors={[]}
                                    eventTitle={`${selectedPolymarketEvent?.displayName} - ${selectedPolymarketMarket.entity}`}
                                    useDecimalScale={true}
                                    onDateRangeChange={(startDate, endDate) => {
                                        setSingleMarketFromDate(startDate);
                                        setSingleMarketToDate(endDate);
                                        setIsSingleMarketZoomed(true);
                                    }}
                                    onResetZoom={() => {
                                        setSingleMarketFromDate(baseSingleMarketFromDate);
                                        setSingleMarketToDate(baseSingleMarketToDate);
                                        setIsSingleMarketZoomed(false);
                                    }}
                                    analysisData={filteredCurrentAnalysisData}
                                    seriesVisibility={seriesVisibility}
                                    isFullSeriesMode={true}
                                    showVolumePanel={volumeVisibility.showVolume}
                                    volumeVisibility={volumeVisibility}
                                    candleFrequencyMs={candleFrequencyMs}
                                />
                            </>
                        )}
                    </Box>
                )}
            </Box>
        </LocalizationProvider>
    );
};

export default OddsTab;
